import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getConfig } from '@local/app-config';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { useNavigate } from 'react-router';
import { useCentralBentleyLicenseTokenMutation, useCentralSeequentLicenseTokenMutation, } from '../../../apiClients/authenticatedIdentityClient/requests/CentralLicenseTokenApi';
import { useLoadCentralInstancesQuery } from '../../../apiClients/entitlementsClient/requests/EntitlementsAPI';
import { LOGIN_ERROR_ROUTE, LOGIN_ROUTE, UUID_REGEX } from '../../../constants';
import { getCombinedToken as getAccessToken, isLoggedInWithBentleyToken, } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { getDeeplink } from '../../../store/sessionStorageHelpers/deeplinkHelper/deeplinkHelper';
import { setSelectedInstanceState } from '../../../store/sessionStorageHelpers/selectedInstanceHelper';
import { Background } from '../../background/Background';
import { CentralBackground } from '../../background/centralBackground/CentralBackground';
import { OrgHubSelectMenu } from '../OrgHubSelectMenu/OrgHubSelectMenu';
import { useStyles } from '../sharedSelector.styles';
import { getPreviousOrDefaultInstance, instances, optionGroups } from '../utils';
export function CentralOrgSelector({ homePagePath = '/' }) {
    const { isLoading, data, error } = useLoadCentralInstancesQuery();
    const navigate = useNavigate();
    const { classes } = useStyles();
    const config = getConfig();
    const [selectedInstance, setSelectedInstance] = useState(null);
    const [userName, setUserName] = useState();
    const [correctIdentityProvider, setCorrectIdentityProvider] = useState(true);
    const [featureLoading, setFeatureLoading] = useState(false);
    const [getCentralSeequentLicenseToken] = useCentralSeequentLicenseTokenMutation();
    const [getCentralBentleyLicenseToken] = useCentralBentleyLicenseTokenMutation();
    const isLoggedInWithBentley = isLoggedInWithBentleyToken();
    useEffect(() => {
        if (error) {
            navigate(`${LOGIN_ERROR_ROUTE}?error=entitlements_failure`);
        }
        if ((data === null || data === void 0 ? void 0 : data.servers.length) === 0) {
            navigate(`${LOGIN_ERROR_ROUTE}?error=no_available_instances`);
        }
        const checkCanBypassOrgSelector = () => __awaiter(this, void 0, void 0, function* () {
            if ((data === null || data === void 0 ? void 0 : data.servers.length) === 1) {
                const instance = data.servers[0];
                return navigateToInstance(instance.host, instance.entitlementId, instance.instanceUUID);
            }
            const destination = getDeeplink(homePagePath);
            const result = destination.match(UUID_REGEX);
            if (result && result[0]) {
                const instance = data === null || data === void 0 ? void 0 : data.servers.find((server) => server.instanceUUID === result[0]);
                if (instance) {
                    return navigateToInstance(instance.host, instance.entitlementId, instance.instanceUUID);
                }
            }
            return undefined;
        });
        checkCanBypassOrgSelector();
    }, [error, data]);
    useEffect(() => {
        const accessToken = getAccessToken();
        const decodedToken = getDecodedIDToken(accessToken);
        if (decodedToken) {
            setUserName(`${decodedToken === null || decodedToken === void 0 ? void 0 : decodedToken.given_name} ${decodedToken === null || decodedToken === void 0 ? void 0 : decodedToken.family_name}`);
        }
        const expired = accessToken && accessToken.expires_at <= Date.now();
        if (expired) {
            navigate(`${LOGIN_ROUTE}`);
        }
    }, []);
    function getDecodedIDToken(accessToken) {
        if (accessToken && accessToken.id_token) {
            return decodeToken(accessToken.id_token);
        }
        return null;
    }
    if (isLoading || (data === null || data === void 0 ? void 0 : data.servers.length) === 1) {
        const Spinner = (_jsx(Grid, { item: true, style: {
                position: 'relative',
                width: '100%',
                height: '100px',
            }, alignContent: "center", justifyContent: "center", display: "grid", children: _jsx(CircularProgress, { "automation-id": "spinner-loading" }) }));
        return config.bentleyIdEnabledCentral ? (_jsx(CentralBackground, { children: Spinner })) : (_jsx(Background, { children: Spinner }));
    }
    function featureCheck(filteredInstance) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`https://${filteredInstance.host}/central/feature`);
            if (!response.ok) {
                throw new Error(`Failed to fetch feature data from: ${filteredInstance.host}`);
            }
            const featureData = yield response.json();
            setFeatureLoading(false);
            if ((featureData.identity_provider === 'Bentley' && isLoggedInWithBentley) ||
                (featureData.identity_provider === 'Seequent' && !isLoggedInWithBentley)) {
                setCorrectIdentityProvider(true);
            }
            else {
                setCorrectIdentityProvider(false);
            }
        });
    }
    function selectInstance() {
        return __awaiter(this, void 0, void 0, function* () {
            const filteredInstance = data === null || data === void 0 ? void 0 : data.servers.find((server) => server.instanceUUID === selectedInstance);
            if (!filteredInstance || !correctIdentityProvider) {
                return undefined;
            }
            const { host, entitlementId, instanceUUID } = filteredInstance;
            return navigateToInstance(host, entitlementId, instanceUUID);
        });
    }
    function navigateToInstance(host, entitlementId, instanceUUID) {
        return __awaiter(this, void 0, void 0, function* () {
            setSelectedInstanceState(host);
            try {
                if (isLoggedInWithBentley) {
                    yield getCentralBentleyLicenseToken(entitlementId).unwrap();
                }
                else {
                    yield getCentralSeequentLicenseToken(entitlementId).unwrap();
                }
            }
            catch (_tokenError) {
                return navigate(`${LOGIN_ERROR_ROUTE}?error=instance_unavailable`);
            }
            const destination = getDeeplink(homePagePath);
            if (destination.includes(instanceUUID)) {
                return navigate(destination);
            }
            return navigate(`/${instanceUUID}${destination}`);
        });
    }
    function selectInstanceWithValidation(instanceUUID) {
        return __awaiter(this, void 0, void 0, function* () {
            const filteredInstance = data === null || data === void 0 ? void 0 : data.servers.find((server) => server.instanceUUID === (instanceUUID !== null && instanceUUID !== void 0 ? instanceUUID : selectedInstance));
            setFeatureLoading(true);
            yield featureCheck(filteredInstance);
        });
    }
    function handleOrgSelect(key) {
        setSelectedInstance(key);
        selectInstanceWithValidation(key);
    }
    return config.bentleyIdEnabledCentral ? (_jsx(CentralBackground, { correctIdentityProvider: correctIdentityProvider, children: _jsxs(Grid, { item: true, xs: 12, className: classes.formContainer, children: [_jsxs(Grid, { className: classes.label, children: [_jsxs(Typography, { children: ["Name: ", userName || null] }), _jsx(Grid, { item: true, xs: true, className: classes.selectMenu, children: _jsx(OrgHubSelectMenu, { options: instances(data), optionGroups: optionGroups(data), onSelected: (key) => handleOrgSelect(key), defaultValue: getPreviousOrDefaultInstance(data), width: 360 }) })] }), _jsx(Button, { color: "primary", variant: "contained", size: "large", className: classes.button, onClick: () => selectInstance(), disabled: featureLoading || !selectedInstance || !correctIdentityProvider, children: featureLoading ? (_jsx(CircularProgress, { size: 24, color: "inherit" })) : ('Continue to Central') })] }) })) : (_jsx(Background, { children: _jsxs(Grid, { item: true, xs: 12, className: classes.formContainer, children: [_jsxs(Grid, { className: classes.label, children: [_jsxs(Typography, { children: ["Name: ", userName || null] }), _jsx(Grid, { item: true, xs: true, className: classes.selectMenu, children: _jsx(OrgHubSelectMenu, { options: instances(data), optionGroups: optionGroups(data), onSelected: (key) => setSelectedInstance(key), defaultValue: getPreviousOrDefaultInstance(data), width: 360 }) })] }), _jsx(Button, { color: "primary", variant: "contained", size: "large", className: classes.button, onClick: () => selectInstance(), disabled: selectedInstance === undefined, children: "Continue to Central" })] }) }));
}
//# sourceMappingURL=CentralOrgSelector.js.map